import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { mockProperties } from '../mock/properties';
import { getProperty } from '../services/api';
import PropertyGallery from '../components/PropertyGallery';
import ContactForm from '../components/ContactForm';
import {
  MapPin, Bed, Bath, Car, Maximize, Calendar, Home as HomeIcon,
  Check, ArrowLeft, Share2, Heart, Building2, Droplets, Zap, Flame, Wifi
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getProperty(id);
        setProperty(data);
      } catch (error) {
        console.error('Error fetching property:', error);
        // Fallback a mock data
        const mockProp = mockProperties.find(p => p.id === id);
        setProperty(mockProp);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="text-xl text-gray-600 font-body">Cargando propiedad...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 font-heading">Propiedad no encontrada</h2>
        <Link to="/" className="text-blue-900 hover:text-amber-600 font-body">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const formatPrice = (price) => {
    // TODAS las propiedades mexicanas (Playa del Carmen, Monterrey) en MXN
    // Propiedades de Tulum en USD (mercado internacional)
    const ciudadesMexicanas = ['Playa del Carmen', 'Monterrey'];
    const esMexicana = ciudadesMexicanas.some(ciudad => 
      property.ciudad?.includes(ciudad)
    );
    
    // Propiedades mexicanas de VENTA: MXN
    if (esMexicana && property.tipo === 'venta') {
      return `$${new Intl.NumberFormat('es-MX').format(price)} MXN`;
    }
    
    // Propiedades de Tulum en VENTA: USD
    if (property.tipo === 'venta') {
      return `$${new Intl.NumberFormat('en-US').format(price)} USD`;
    }
    
    // Rentas: MXN
    return `$${new Intl.NumberFormat('es-MX').format(price)} MXN`;
  };

  const serviciosIcons = {
    agua: <Droplets size={20} />,
    luz: <Zap size={20} />,
    gas: <Flame size={20} />,
    internet: <Wifi size={20} />
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        {/* Botón volver */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-blue-900 hover:text-amber-600 font-medium mb-6 transition-colors font-body"
        >
          <ArrowLeft size={20} />
          Volver a Propiedades
        </Link>

        {/* Header con precio y acciones */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase ${
                  property.tipo === 'venta' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {property.tipo}
                </span>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 font-body">
                  {property.tipoPropiedad}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-3 font-heading">
                {property.titulo}
              </h1>
              <div className="flex items-center gap-2 text-gray-600 mb-4">
                <MapPin className="text-amber-600" size={20} />
                <span className="text-lg font-body">
                  {property.direccion}, {property.colonia}, {property.ciudad}, {property.estado}
                </span>
              </div>
              <div className="text-4xl font-bold text-amber-600 font-heading">
                {formatPrice(property.precio)}
                {property.tipo === 'renta' && <span className="text-xl text-gray-500 font-body">/mes</span>}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex items-center gap-2 font-body">
                <Heart size={20} />
                Guardar
              </Button>
              <Button variant="outline" className="flex items-center gap-2 font-body">
                <Share2 size={20} />
                Compartir
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Galería */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 font-heading">Galería de Fotos y Videos</h2>
              <PropertyGallery fotos={property.fotos} videos={property.videos} titulo={property.titulo} />
            </div>

            {/* Características principales */}
            {property.tipoPropiedad !== 'Terreno' && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 font-heading">Características Principales</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {property.habitaciones > 0 && (
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Bed className="text-blue-900 mx-auto mb-2" size={32} />
                      <p className="text-2xl font-bold text-blue-900 font-heading">{property.habitaciones}</p>
                      <p className="text-sm text-gray-600 font-body">Habitaciones</p>
                    </div>
                  )}
                  {property.banosCompletos > 0 && (
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Bath className="text-blue-900 mx-auto mb-2" size={32} />
                      <p className="text-2xl font-bold text-blue-900 font-heading">{property.banosCompletos}</p>
                      <p className="text-sm text-gray-600 font-body">Baños</p>
                    </div>
                  )}
                  {property.estacionamientos > 0 && (
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Car className="text-blue-900 mx-auto mb-2" size={32} />
                      <p className="text-2xl font-bold text-blue-900 font-heading">{property.estacionamientos}</p>
                      <p className="text-sm text-gray-600 font-body">Estacionamientos</p>
                    </div>
                  )}
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Maximize className="text-blue-900 mx-auto mb-2" size={32} />
                    <p className="text-2xl font-bold text-blue-900 font-heading">{property.areaTotal}</p>
                    <p className="text-sm text-gray-600 font-body">m² Totales</p>
                  </div>
                </div>
              </div>
            )}

            {/* Descripción */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 font-heading">Descripción</h2>
              <p className="text-gray-700 leading-relaxed font-body">{property.descripcion}</p>
            </div>

            {/* Detalles completos */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 font-heading">Detalles Completos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {property.areaConstruida > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <HomeIcon className="text-blue-900" size={24} />
                    <div>
                      <p className="text-sm text-gray-600 font-body">Área Construida</p>
                      <p className="font-bold text-gray-800 font-heading">{property.areaConstruida} m²</p>
                    </div>
                  </div>
                )}
                {property.mediosBanos > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Bath className="text-blue-900" size={24} />
                    <div>
                      <p className="text-sm text-gray-600 font-body">Medios Baños</p>
                      <p className="font-bold text-gray-800 font-heading">{property.mediosBanos}</p>
                    </div>
                  </div>
                )}
                {property.niveles > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Building2 className="text-blue-900" size={24} />
                    <div>
                      <p className="text-sm text-gray-600 font-body">Niveles</p>
                      <p className="font-bold text-gray-800 font-heading">{property.niveles}</p>
                    </div>
                  </div>
                )}
                {property.anioConstruccion && (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <Calendar className="text-blue-900" size={24} />
                    <div>
                      <p className="text-sm text-gray-600 font-body">Año de Construcción</p>
                      <p className="font-bold text-gray-800 font-heading">{property.anioConstruccion}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Amenidades */}
            {property.amenidades && property.amenidades.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 font-heading">Amenidades</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.amenidades.map((amenidad, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="text-green-600 flex-shrink-0" size={20} />
                      <span className="text-gray-700 font-body">{amenidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Características especiales */}
            {property.caracteristicasEspeciales && property.caracteristicasEspeciales.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 font-heading">Características Especiales</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.caracteristicasEspeciales.map((caracteristica, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="text-amber-600 flex-shrink-0" size={20} />
                      <span className="text-gray-700 font-body">{caracteristica}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Servicios */}
            {property.servicios && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 font-heading">Servicios Disponibles</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(property.servicios).map(([servicio, disponible]) => (
                    disponible && (
                      <div key={servicio} className="flex flex-col items-center gap-2 p-4 bg-blue-50 rounded-lg">
                        <div className="text-blue-900">
                          {serviciosIcons[servicio] || <Check size={20} />}
                        </div>
                        <span className="text-sm font-medium text-gray-700 capitalize font-body">
                          {servicio}
                        </span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Formulario de contacto */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <ContactForm propertyTitle={property.titulo} propertyId={property.id} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
